import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/statuscake')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'StatusCake Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs StatusCake: 30-second checks from $3/mo vs $79.99/mo. Compare plans, check intervals, alert confirmation, and status pages." },
      { property: 'og:title', content: 'StatusCake Alternative — Uptrack' },
      { property: 'og:description', content: "StatusCake checks every 30s on its Business plan. Uptrack checks every 30s on every plan from $3/mo. Compare features." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-statuscake.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/statuscake' },
    ],
    scripts: comparisonScripts('statuscake', "StatusCake"),
  }),
})

const data: ComparisonData = {
  competitor: "StatusCake",
  tagline: "StatusCake offers uptime, page speed, SSL, domain, and server monitoring, with a free plan of 10 monitors. Uptrack checks every 30 seconds on every plan, from $3/mo.",
  heroDescription: "These facts come from StatusCake's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "10 monitors at 5 min" },
    { feature: "Cheapest paid plan", uptrack: "Pro $3/mo: 50 monitors at 30s", competitor: "Superior $24.49/mo: 100 monitors at 1 min", uptrackWins: true },
    { feature: "30-second checks", uptrack: "Every plan, from $3/mo", competitor: "Business plan, $79.99/mo", uptrackWins: true },
    { feature: "Down confirmation", uptrack: "2-5 consecutive failed checks; several check locations must confirm", competitor: "2 confirmation servers by default (0-3)" },
    { feature: "Team members (cheapest paid plan)", uptrack: "3 on Pro", competitor: "9 on Superior" },
    { feature: "Page speed monitoring", uptrack: "Not available", competitor: "Included" },
    { feature: "Server monitoring", uptrack: "Not available", competitor: "Included" },
  ],

  painPoints: [
    {
      title: "30-second checks start at $79.99/mo",
      description: "StatusCake's free plan checks every 5 minutes and Superior checks every minute. 30-second checks start on the Business plan at $79.99/mo ($66.66/mo billed annually). Every Uptrack plan checks every 30 seconds.",
    },
    {
      title: "Entry price",
      description: "StatusCake Superior is $24.49/mo ($20.41/mo billed annually) for 100 monitors. Uptrack Pro is $3/mo for 50 monitors, and Team is $20/mo for 200.",
    },
    {
      title: "How downtime is confirmed",
      description: "StatusCake confirms a failure from 2 more servers by default, and an optional trigger delay can hold the alert. Uptrack waits for 2-5 consecutive failed checks, and several check locations must confirm.",
    },
  ],

  differentiators: [
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "30-second checks on every plan: Pro is $3/mo for 50 monitors, Team is $20/mo for 200, Business is $79/mo for 1,000.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Status pages on every plan: 1 on free, 5 on Pro, unlimited on Team and Business, with email subscriber notifications on paid plans.",
    "Flat, published pricing: $3, $20, or $79 per month. No per-seat or usage charges, and a 30-day money-back guarantee.",
  ],

  notForYou: [
    "If you need page speed monitoring, StatusCake has it and Uptrack doesn't.",
    "If you need server monitoring (CPU, RAM, disk), StatusCake offers it as part of its platform.",
    "If you need more seats on an entry plan, StatusCake Superior includes 9 team members. Uptrack Pro includes 3.",
    "If you need more than 5 free monitors, StatusCake offers 10 monitors at 5-minute checks. Uptrack's free plan is 5 monitors at 30 seconds from one location.",
  ],
}
